import { FlexCol, FlexRow } from '@/components/common/Flex';
import Icon from '@/components/common/icons/Icon';
import KeyValueStack, {
    KeyValueStackItem,
} from '@/components/common/KeyValueStack';
import { Paragraph, Text } from '@/components/common/Typography';
import AccountLockup from '@/components/financialEntities/AccountLockup';
import { useSelectableDepositoryAccounts } from '@/components/financialEntities/helpers';
import { toDollars } from '@/util/currency';
import { Center, Spinner, StackProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { useAtomicStatus } from '@common/react/atomic';
import {
    getAmountRecurrenceStr,
    getContributionOrInvestmentStr,
    iconByGoalAccountInvestmentType,
    infoByGoalAccountStructure,
    titleByGoalAccountInvestmentType,
} from '@common/react/goals';
import { useCurrentUser } from '@common/react/user';
import { dt } from '@common/util/dateTime';
import { useGoalOnboardingContext } from './GoalOnboardingContext';

export interface GoalOnboardingConfirmKeyValueStackProps extends StackProps {}

const GoalOnboardingConfirmKeyValueStack = ({
    ...props
}: GoalOnboardingConfirmKeyValueStackProps) => {
    const { activePartnershipId, plentyJointAccountStatus } = useCurrentUser();
    const atomicStatus = useAtomicStatus();
    const { accounts } = useSelectableDepositoryAccounts();
    const { goal, setStep, goalOnboardingData } = useGoalOnboardingContext();
    const {
        fundingAccountId,
        initialDepositPlan,
        transactionSchedulePlan,
        investmentType,
    } = goalOnboardingData;
    assert(investmentType, 'Missing investmentType');
    const fundingAccount =
        fundingAccountId && accounts.find((a) => a.id === fundingAccountId);

    if (!atomicStatus || !fundingAccount || !plentyJointAccountStatus) {
        return (
            <Center h="full" w="full">
                <Spinner color="gray.400" />
            </Center>
        );
    }

    const isAccountComplete =
        goal.accountStructure === 'JOINT'
            ? plentyJointAccountStatus === 'OPENED'
            : atomicStatus?.status === 'ACCOUNT_COMPLETE';
    const initialDepositAmountStr = toDollars(initialDepositPlan);

    const recurringInvestmentStr = getAmountRecurrenceStr(
        transactionSchedulePlan
    );

    const contributionOrInvestmentStr =
        getContributionOrInvestmentStr(investmentType);

    // Compose items list, omitting the "initial investment" row if initial
    // deposit amount is $0 and/or the "recurring investment" row if the
    // transaction schedule plan amount is $0
    const items: KeyValueStackItem[] = [
        {
            key: 'Source account',
            value: (
                <AccountLockup
                    account={fundingAccount}
                    maxW={52}
                    justifyContent="flex-start"
                />
            ),
            onClick: () => setStep('CONNECT'),
        },
    ];

    if (initialDepositPlan > 0) {
        const dateStr = isAccountComplete ? 'today' : 'account opening';
        items.push({
            key: `Initial ${contributionOrInvestmentStr}`,
            value: (
                <FlexCol>
                    <Paragraph>{initialDepositAmountStr}</Paragraph>
                    <Paragraph as="span" fontSize="sm" color="textMuted">
                        within 1-2 business days of {dateStr}
                    </Paragraph>
                </FlexCol>
            ),
            onClick: () => setStep('PLAN'),
        });
    }

    if (transactionSchedulePlan.amount > 0) {
        // Just to line up the "today" string with initial deposit.
        // TODO: consider formalizing this logic more broadly.
        const { startDate } = transactionSchedulePlan;
        const startDateFromNow = dt(startDate).fromNowDays();
        const recurringInvestmentStartDateStr =
            startDateFromNow === 'today' || startDateFromNow === 'tomorrow'
                ? isAccountComplete
                    ? startDateFromNow
                    : 'account opening'
                : dt(startDate).format('MMMM D, YYYY');

        items.push({
            key: `Repeating ${contributionOrInvestmentStr}`,
            value: (
                <FlexCol>
                    <Paragraph>{recurringInvestmentStr}</Paragraph>
                    <Paragraph as="span" fontSize="sm" color="textMuted">
                        starts within 1-2 business days of{' '}
                        {recurringInvestmentStartDateStr}
                    </Paragraph>
                </FlexCol>
            ),
            onClick: () => setStep('PLAN'),
        });
    }

    const goalAccountStructureInfo =
        infoByGoalAccountStructure[goal.accountStructure];

    items.push({
        key: 'Goal type',
        value: (
            <FlexCol>
                <FlexRow align="center" gap={2}>
                    <Icon
                        name={goalAccountStructureInfo.iconName}
                        color="blue.400"
                        size={10}
                    />
                    <Text>{goalAccountStructureInfo.title} account</Text>
                </FlexRow>
                <Text fontSize="sm" color="textMuted">
                    {goalAccountStructureInfo.description({
                        activePartnershipId,
                    })}
                </Text>
            </FlexCol>
        ),
        onClick: () => setStep('ACCOUNT_TYPE'),
    });

    const title = titleByGoalAccountInvestmentType[investmentType];
    const subtitle = {
        HIGH_YIELD_CASH: 'BNY Mellon Dreyfus Government Money Market Fund',
        DIRECT_INDEXING:
            'A diversified, direct indexing based stock and bond portfolio',
        CLASSIC_PORTFOLIO: 'A diversified, ETF based stock and bond portfolio',
    }[investmentType];
    const iconName = iconByGoalAccountInvestmentType[investmentType];

    items.push({
        key: 'Investment type',
        value: (
            <FlexCol>
                <FlexRow alignItems="center">
                    <Icon name={iconName} size="sm" mr={2} />
                    <Paragraph>{title}</Paragraph>
                </FlexRow>
                <Paragraph fontSize="sm" color="textMuted">
                    {subtitle}
                </Paragraph>
            </FlexCol>
        ),
        onClick: () => setStep('PORTFOLIO'),
    });

    return (
        <KeyValueStack
            items={items}
            itemProps={{ py: 4 }}
            valueAlign="left"
            withEndLine
            {...props}
        />
    );
};

export default GoalOnboardingConfirmKeyValueStack;
